import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import PageHero from "../components/pageHero"
import ProductBreadcrumbs from "../components/productBreadcrumbs"
import Img from "gatsby-image"
import {
  Box,
  Container,
  Divider,
  Grid,
  Heading,
  Text,
} from "@theme-ui/components"
import ProductThumb from "../components/productThumb"
import { LangContext } from "../context/langContext"
import { ExternalButtonLinkSecondary } from "../components/buttons"
import CsvTable from "../components/csvTable"
import VideoEmbed from "../components/videoEmbed"
import { getProductPath } from "../utils/pathUtils"

const Product = ({ data: { product } }) => {
  const i18nPaths = product._allSlugLocales.map(locale => {
    return {
      locale: locale.locale,
      value: getProductPath(product, locale.locale),
    }
  })

  return (
    <Layout locale={product.locale} i18nPaths={i18nPaths}>
      <LangContext.Consumer>
        {t => (
          <>
            <SEO title={product.name} lang={product.locale} />
            <PageHero
              title={product.name}
              image={product.heroImage}
              locale={product.locale}
            >
              <ProductBreadcrumbs product={product} />
            </PageHero>
            <Container mt={[5]}>
              <Grid columns={[1, 2]} gap={[3, 4, 6]}>
                <Box>
                  {product.image && <Img fluid={product.image.fluid} />}
                </Box>
                <Box>
                  <Box mb={4}>
                    <Heading as="p" variant="displaySmall" mb={4}>
                      {product.name}
                    </Heading>
                    <Divider
                      sx={{
                        width: "50px",
                        backgroundColor: "primary",
                        color: "primary",
                        height: "10px",
                      }}
                    />
                    {product.summary && (
                      <Heading
                        as="h2"
                        variant="lead"
                        sx={{ fontSize: 4, textTransform: "initial" }}
                        mt={4}
                      >
                        {product.summary}
                      </Heading>
                    )}
                  </Box>
                  {product.regulation && (
                    <Box mb={4}>
                      <Heading as="p" variant="headingSmall" mb={2}>
                        {t.standard}
                      </Heading>
                      <Text>{product.regulation}</Text>
                    </Box>
                  )}
                  {product.description && (
                    <Box mb={4}>
                      <Heading as="p" variant="headingSmall" mb={3}>
                        {t.features}
                      </Heading>
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: product.description,
                        }}
                      />
                    </Box>
                  )}
                  {product.catalogue && (
                    <Box mb={3}>
                      <ExternalButtonLinkSecondary href={product.catalogue.url}>
                        {t.downloadCatalogue}
                      </ExternalButtonLinkSecondary>
                    </Box>
                  )}
                  {product.pdfTable && (
                    <Box mb={3}>
                      <ExternalButtonLinkSecondary href={product.pdfTable.url}>
                        {t.downloadTable}
                      </ExternalButtonLinkSecondary>
                    </Box>
                  )}
                  {product.packagingImage && (
                    <Box mb={3}>
                      <ExternalButtonLinkSecondary
                        href={product.packagingImage.fluid.src}
                      >
                        {t.downloadPackagingImage}
                      </ExternalButtonLinkSecondary>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Container>
            <Container>
              {product.table && <CsvTable url={product.table.url} />}
              {product.tableLegend && (
                <Box
                  mb={5}
                  dangerouslySetInnerHTML={{ __html: product.tableLegend }}
                />
              )}
              {product.packagingImage && (
                <Box mb={5}>
                  <SectionTitle title="Packaging" />
                  <Img fluid={product.packagingImage.fluid} />
                </Box>
              )}
              {product.videoEmbed && (
                <Box mb={5}>
                  <SectionTitle title="Video" />
                  <VideoEmbed embedCode={product.videoEmbed} />
                </Box>
              )}
              {product.relatedProducts.map(category => (
                <Box key={category.id} mb={5}>
                  <SectionTitle title={category.title} />
                  <Grid gap={4} columns={[1, 2, 3, 4]}>
                    {category.products.map(product => (
                      <ProductThumb product={product} key={product.id} />
                    ))}
                  </Grid>
                </Box>
              ))}
            </Container>
          </>
        )}
      </LangContext.Consumer>
    </Layout>
  )
}

const SectionTitle = ({ title }) => (
  <Box mb={4}>
    <Heading as="h3" variant="displaySmall" sx={{ textAlign: "center" }} pb={3}>
      {title}
    </Heading>
    <Divider
      sx={{
        width: "52px",
        height: "10px",
        margin: "auto",
        color: "primary",
        backgroundColor: "primary",
      }}
    />
  </Box>
)

export default Product

export const query = graphql`
  query ProductQuery($id: String!) {
    product: datoCmsProduct(id: { eq: $id }) {
      id
      name
      locale
      ...allProductSlugLocales
      description
      summary
      regulation
      videoEmbed
      catalogue {
        url
      }
      heroImage {
        fluid(maxWidth: 1400) {
          ...GatsbyDatoCmsFluid
        }
      }
      image {
        fluid(maxWidth: 700) {
          ...GatsbyDatoCmsFluid
        }
      }
      packagingImage {
        fluid(maxWidth: 1110) {
          ...GatsbyDatoCmsFluid
        }
      }
      table {
        url
      }
      pdfTable {
        url
      }
      tableLegend
      category {
        name
        slug
        root
        locale
        ...AllProductCategorySlugLocales
        treeParent {
          name
          slug
          root
          locale
          ...AllProductCategorySlugLocales
          treeParent {
            name
            slug
            root
            locale
            ...AllProductCategorySlugLocales
          }
        }
      }
      relatedProducts {
        title
        id
        products {
          id
          slug
          name
          locale
          ...allProductSlugLocales
          summary
          description
          image {
            fluid(maxWidth: 700) {
              ...GatsbyDatoCmsFluid
            }
          }
          category {
            slug
            root
            locale
            ...AllProductCategorySlugLocales
            treeParent {
              slug
              root
              locale
              ...AllProductCategorySlugLocales
              treeParent {
                slug
                root
                locale
                ...AllProductCategorySlugLocales
              }
            }
          }
        }
      }
    }
  }

  fragment allProductSlugLocales on DatoCmsProduct {
    _allSlugLocales {
      value
      locale
    }
  }
`
